import { Body, Text, cn, useRebrand } from '@growtherapy/sprout-ui';

import { ModifiedToastAria, Toast, ToastVariant } from '../../toasts';
import { ToastButton } from '../../toasts/ToastButton';

type CSSRSPromptToastProps = {
  onAdministerCSSRS: () => void;
  heading?: string;
  body?: string;
  variant?: 'alert' | 'info';
  callToAction: string;
} & ModifiedToastAria;

export function CSSRSPromptToast({
  contentProps,
  titleProps,
  descriptionProps,
  onAdministerCSSRS,
  heading,
  body,
  variant = 'alert',
  callToAction,
  ...toastAriaProps
}: CSSRSPromptToastProps) {
  const isRebrand = useRebrand();
  const isInfo = variant === 'info';
  const toastVariant = isInfo ? ToastVariant.Info : ToastVariant.Error;

  return (
    <Toast
      alignment='top'
      className='max-w-4xl w-full sm:w-full'
      closeButtonSize='sm'
      contentClassName='rebrand:items-start rebrand:md:items-center'
      variant={toastVariant}
      {...toastAriaProps}
    >
      <div className='flex flex-1 flex-col md:flex-row justify-start gap-3 min-w-0 w-full md:items-start rebrand:md:items-center'>
        <div {...contentProps} className={cn('flex min-w-0 w-full flex-col gap-1 md:w-max', contentProps?.className)}>
          {isRebrand ? (
            <>
              <Body.Default
                as='h3'
                {...titleProps}
                className={cn(
                  '!font-semibold',
                  isInfo ? '!text-text-default' : '!text-status-error-text',
                  titleProps.className,
                )}
              >
                {heading}
              </Body.Default>
              <Body.Small
                as='span'
                {...descriptionProps}
                className={cn(
                  isInfo ? 'text-text-default' : 'text-status-error-text',
                  descriptionProps.className,
                  'fs-exclude data-dd-privacy',
                )}
              >
                {body}
              </Body.Small>
            </>
          ) : (
            <>
              <Text
                as='h3'
                {...titleProps}
                className={cn(
                  'font-medium',
                  isInfo ? 'text-text-default' : 'text-status-error-text',
                  titleProps.className,
                  'fs-exclude data-dd-privacy',
                )}
              >
                {heading}
              </Text>
              <Text
                as='span'
                {...descriptionProps}
                className={cn(
                  'text-sm',
                  isInfo ? 'text-text-default' : 'text-status-error-text',
                  descriptionProps.className,
                )}
              >
                {body}
              </Text>
            </>
          )}
        </div>

        <ToastButton
          className='self-start md:self-auto rebrand:max-h-none rebrand:shrink-0'
          use={isRebrand ? (isInfo ? 'primary' : 'destructive') : 'secondary'}
          onClick={onAdministerCSSRS}
        >
          {callToAction}
        </ToastButton>
      </div>
    </Toast>
  );
}
